// PHASE_1 / B3 — virtualized (windowed) org chart (the phase EXIT GATE).
//
// Spec 68 non_functional + BUILD_PLAN §5 "Org chart render — windowed/
// virtualized": ~1000 nodes must render without freezing; up to 5000 visible
// nodes before virtualization is mandatory, 50k/tenant with it. This component
// renders ONLY the rows in the viewport — at 1000 nodes the DOM holds ~30 rows,
// not 1000 — so it stays smooth without any external virtualization dependency
// (avoids a shared package.json edit; the windowing is a fixed-row-height
// scroll calculation + a spacer, the classic technique @tanstack/react-virtual
// generalizes).
//
// Tree model: the flat node list (solid-line manager_id only — ADR-012 keeps
// solid-line the single authority; dotted-line rows are NOT part of the chart
// skeleton) is indexed parent→children, then flattened into a LINEAR list of
// visible rows honoring the `expanded` set (lazy-expand: a collapsed subtree's
// descendants are never in the flattened list, so they cost nothing). Toggling
// a node re-flattens — O(visible), not O(tree).
//
// The component is shell-agnostic and i18n-key-driven (labels passed in); it
// owns no fetching. It is the windowed renderer the org-chart-content screen
// mounts. No money anywhere.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { PersonLink } from "@/components/person-link";

import type { OrgChartNode } from "./api";
import { flatten, indexTree, windowSlice } from "./org-tree-flatten";
import {
  ORG_VIEW_KEYS,
  readViewState,
  reviveIdList,
  reviveNumber,
  writeViewState,
} from "./org-view-state";

/** Fixed row height (px) — windowing math depends on a uniform row height. */
const ROW_HEIGHT = 44;
/** Rows rendered above/below the viewport to avoid blank flashes on fast scroll. */
const OVERSCAN = 8;
/** Indent per tree depth level (px). */
const INDENT_PX = 20;

// Decorative toggle carets — locale-neutral symbols, NOT translatable words.
// Held as constants so the JSX carries no literal text (ADR-006 lint); the
// accessible expand/collapse labels ride on the button's aria-label instead.
const CARET_EXPANDED = "−"; // minus sign
const CARET_COLLAPSED = "+";

export interface OrgChartLabels {
  /** Aria label for the scroll region. */
  readonly regionLabel: string;
  /** Expand-node aria label. */
  readonly expandLabel: string;
  /** Collapse-node aria label. */
  readonly collapseLabel: string;
  /** Shown for a node with no job title. */
  readonly noTitle: string;
  /** Empty-state text when the tenant has no chart nodes. */
  readonly empty: string;
  /** Direct-report count suffix, e.g. "{{count}} doğrudan rapor". */
  readonly reportsLabel: (count: number) => string;
}

export interface OrgChartProps {
  readonly nodes: readonly OrgChartNode[];
  readonly labels: OrgChartLabels;
  /** Viewport height (px). Defaults to 560 (~12 visible rows). */
  readonly height?: number;
}

export function OrgChart({
  nodes,
  labels,
  height = 560,
}: OrgChartProps): React.ReactElement {
  const { childrenOf, roots } = useMemo(() => indexTree(nodes), [nodes]);

  // Roots expanded by default so the top of the org is visible without a click;
  // deeper subtrees lazy-expand on demand. A set left behind by an earlier visit
  // outranks that default — the reader is returning, not arriving (SEM-6349).
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => {
    const stored = readViewState(ORG_VIEW_KEYS.chartExpanded, reviveIdList);
    if (stored !== null) return new Set(stored);
    const init = new Set<string>();
    for (const r of roots) init.add(r.employeeId);
    return init;
  });
  const [scrollTop, setScrollTop] = useState(
    () => readViewState(ORG_VIEW_KEYS.chartScrollTop, reviveNumber) ?? 0,
  );

  useEffect(() => {
    writeViewState(ORG_VIEW_KEYS.chartExpanded, [...expanded]);
  }, [expanded]);
  useEffect(() => {
    writeViewState(ORG_VIEW_KEYS.chartScrollTop, scrollTop);
  }, [scrollTop]);

  const rows = useMemo(
    () => flatten(roots, childrenOf, expanded),
    [roots, childrenOf, expanded],
  );

  const toggle = useCallback((employeeId: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  }, []);

  const scrollRef = useRef<HTMLDivElement>(null);

  // The restored offset drives the windowing maths from the first render, but
  // the scroll container itself still opens at 0 — it has to be told once, on
  // mount. Read through a ref so later scrolling (which the DOM owns) cannot
  // re-run this and yank the reader back to where they came in.
  const restoredScrollTop = useRef(scrollTop).current;
  useLayoutEffect(() => {
    if (scrollRef.current !== null) scrollRef.current.scrollTop = restoredScrollTop;
  }, [restoredScrollTop]);

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>): void => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  if (nodes.length === 0) {
    return (
      <p data-slot="org-chart-empty" className="text-sm text-[var(--fg-muted)]">
        {labels.empty}
      </p>
    );
  }

  // Windowing: only render the slice of rows intersecting the viewport (+overscan).
  const total = rows.length;
  const { startIndex, endIndex } = windowSlice({
    total,
    scrollTop,
    viewportHeight: height,
    rowHeight: ROW_HEIGHT,
    overscan: OVERSCAN,
  });
  const windowRows = rows.slice(startIndex, endIndex);

  return (
    <div
      ref={scrollRef}
      role="tree"
      aria-label={labels.regionLabel}
      data-slot="org-chart"
      data-node-count={total}
      onScroll={onScroll}
      style={{ height, overflowY: "auto", position: "relative" }}
      className="rounded-[12px] border border-[var(--border-subtle)] bg-[var(--bg-raised)]"
    >
      {/* Spacer reserves the full scroll height so the scrollbar reflects the
          whole tree even though only a window is in the DOM. */}
      <div style={{ height: total * ROW_HEIGHT, position: "relative" }}>
        {windowRows.map((row, i) => {
          const absoluteIndex = startIndex + i;
          // Direct-report count for the novem count pill (the mockup's {dolu}
          // chip) — read from the same adjacency the flatten walk uses, so it
          // costs nothing extra.
          const childCount = childrenOf.get(row.node.employeeId)?.length ?? 0;
          return (
            <div
              key={row.node.employeeId}
              role="treeitem"
              aria-level={row.depth + 1}
              aria-selected={false}
              aria-expanded={row.hasChildren ? row.expanded : undefined}
              data-slot="org-chart-row"
              style={{
                position: "absolute",
                top: absoluteIndex * ROW_HEIGHT,
                left: 0,
                right: 0,
                height: ROW_HEIGHT,
                paddingLeft: 10 + row.depth * INDENT_PX,
              }}
              className="flex items-center gap-2 border-b border-[var(--border-subtle)] px-2.5"
            >
              {row.hasChildren ? (
                <button
                  type="button"
                  onClick={(): void => toggle(row.node.employeeId)}
                  aria-label={row.expanded ? labels.collapseLabel : labels.expandLabel}
                  data-slot="org-chart-toggle"
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[13px] text-[var(--fg-muted)] hover:bg-[var(--accent-subtle-bg)] hover:text-[var(--accent)]"
                >
                  {/* Decorative caret — the accessible label rides on aria-label
                      above; the glyph itself is hidden from the a11y tree. */}
                  <span aria-hidden="true">
                    {row.expanded ? CARET_EXPANDED : CARET_COLLAPSED}
                  </span>
                </button>
              ) : (
                <span className="h-6 w-6 shrink-0" aria-hidden="true" />
              )}
              {/* novem kind-dot: a filled emerald square for a node with reports,
                  a hollow oat dot for an individual contributor (leaf). */}
              <span
                aria-hidden="true"
                className="h-[9px] w-[9px] shrink-0 rounded-[3px]"
                style={{
                  background: row.hasChildren
                    ? "var(--accent)"
                    : "var(--border-default)",
                }}
              />
              {/* An `<a href>`, not a click handler: right-click → open in a
                  new tab is how a reader inspects one person without giving up
                  the chart, and the address comes from the single person-link
                  producer (SEM-6329) rather than a path spelled out here. */}
              <PersonLink
                employeeId={row.node.employeeId}
                className="flex min-w-0 flex-1 items-baseline gap-2 truncate text-left"
              >
                <span className="truncate font-serif text-[14px] text-[var(--fg-primary)]">
                  {row.node.displayName}
                </span>
                <span className="truncate font-sans text-xs text-[var(--fg-muted)]">
                  {row.node.jobTitle ?? labels.noTitle}
                </span>
              </PersonLink>
              {/* text-xs (12px), not the mockup's 10.5px: the count is the
                  row's only quantitative content, so it must not sit below
                  the 12px information-text floor. */}
              {childCount > 0 && (
                <span
                  data-slot="org-chart-report-count"
                  aria-label={labels.reportsLabel(childCount)}
                  className="shrink-0 rounded-full bg-[var(--bg-subtle)] px-[7px] py-[1px] font-sans text-xs font-semibold text-[var(--fg-secondary)]"
                >
                  {childCount}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
